import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  onClose?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * 智能剪辑面板错误边界（数据保护双保险之二）：
 * 面板内渲染错误在此拦截，不再冒泡到 main.tsx 顶层边界导致整个应用被卸载
 * （画布节点随之全部丢失）。数据本身由 SmartEditPanel 卸载保全负责——
 * React 卸载面板子树时先执行其清理函数，把未保存编辑静默写回节点后才落到本边界。
 * 渲染错误只影响面板本身，节点数据与画布始终安全。
 */
export class SmartEditErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[SmartEditErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    // 重试：重置边界状态重新挂载面板；editDocument 已写回节点数据，重开不丢
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#09090b]">
          <div className="flex flex-col items-center gap-5 p-8 max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertTriangle size={28} className="text-red-400" />
            </div>
            <div>
              <h3 className="text-base font-medium text-zinc-200 mb-2">智能剪辑面板发生错误</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">
                面板遇到了意外错误。未保存的编辑已自动写回节点，数据不会丢失；
                画布与其他功能不受影响。
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={this.handleReload}
                className="px-5 py-2 text-sm font-medium text-zinc-200 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700/60 rounded-lg transition-colors"
              >
                重新加载
              </button>
              {this.props.onClose && (
                <button
                  onClick={this.props.onClose}
                  className="px-5 py-2 text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  返回画布
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
